import {
  motion,
  AnimatePresence,
  useScroll,
  useTransform,
  useInView,
  animate,
} from "framer-motion";
import { useRef, useState, useEffect, lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import HeroGallery from "@/components/HeroGallery";
import InstallCards from "@/components/InstallCards";
import BeforeAfterSlider, { TripleImageSlider } from "@/components/BeforeAfterSlider";
import { IMAGES, SERVICES, INDUSTRIES, STATS, PROCESS } from "@/lib/content";
import progressStage1 from "@/assets/home-progress/kleenclad.webp";
import progressStage2 from "@/assets/home-progress/klneed2.webp";
import progressStage3 from "@/assets/home-progress/antimicro2.webp";
import beforeSet1 from "@/assets/home-progress/stage-1-fitout.webp";
import afterSet1 from "@/assets/home-progress/stage-2-complete.webp";
import beforeSet2 from "@/assets/home-progress/stage-3-fitted.webp";
import afterSet2 from "@/assets/home-progress/after-set2.webp";

// Lazy -- three.js/@react-three/fiber are heavy and only needed for this
// decorative animation, so they get their own chunk instead of bloating
// the main bundle every page pays for.
const Hero3D = lazy(() => import("@/components/Hero3D"));

const PROGRESS_SLIDES = [
  {
    id: "stage-triple",
    kind: "triple",
    images: [progressStage1, progressStage2, progressStage3],
    alts: [
      "Cladding installation site during fit-out",
      "Cladding installation nearing completion",
      "Completed cladding installation, fully fitted",
    ],
  },
  {
    id: "set-1",
    kind: "triple",
    images: [beforeSet1, afterSet1, beforeSet2],
    alts: [
      "Cladding installation site during fit-out",
      "Cladding installation nearing completion",
      "Completed cladding installation, fully fitted",
    ],
  },
  // { id: "set-1", kind: "before-after", before: beforeSet1, after: afterSet1 },
  
];
const PROGRESS_TABS = ["Kleenclad", "Bioclad"];
const PROGRESS_SLIDE_DURATION = 5000;

function Molecular3DBreak() {
  return (
    <section
      data-testid="home-molecular"
      className="relative w-full overflow-hidden section-dark py-10 md:py-16"
    >
      {/* Grain */}
      <div className="absolute inset-0 z-10 grain pointer-events-none" />

      <div className="relative z-20 w-full max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 items-center gap-8">
        <div className="md:col-span-5">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: [0.19, 1, 0.22, 1] }}
            className="overline text-[#189A96]"
          >
            About GoHealthe
          </motion.p>

          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.1, ease: [0.19, 1, 0.22, 1] }}
            className="font-serif text-4xl md:text-6xl mt-6 leading-[1.05] text-[#F8FAFC]"
          >
            India's{" "}
            <span className="italic text-[#189A96]">
              First Antimicrobial Cladding Installation
            </span>{" "}
            Company
          </motion.h2>
        </div>

        <div className="md:col-span-6 md:col-start-7 mt-8 md:mt-0">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.2, ease: [0.19, 1, 0.22, 1] }}
            className="text-[#F8FAFC]/70 font-sans text-[19px] leading-relaxed"
          >
            GoHealthe is recognised as India's first antimicrobial wall
            cladding installation company and one of the country's oldest
            specialist cladding installers. Since 2010, we have delivered
            precision installation solutions for hospitals, pharmaceutical
            facilities, cleanrooms, laboratories and food processing
            environments, creating hygienic spaces built for long-term
            performance.
          </motion.p>
        </div>
      </div>
    </section>
  );
}

function Marquee() {
  const items = [
    "Silver-ion protection",
    "NABH compliant",
    "GMP cleanroom certified",
    "Seamless welded joins",
    "14-year performance",
    "Live-site installation",
  ];
  return (
    <div
      data-testid="home-marquee"
      className="section-dark py-6 border-y border-white/10 overflow-hidden"
    >
      <div className="marquee-track">
        {[...items, ...items, ...items].map((t, i) => (
          <span
            key={i}
            className="font-serif text-3xl md:text-5xl px-10 text-[#F8FAFC]/70 whitespace-nowrap italic"
          >
            {t} <span className="text-[#189A96] mx-4">✦</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function ValueProp() {
  const animRef = useRef(null);
  // Not just code-split -- deferred until this section actually scrolls
  // into view, so the heavy three.js chunk doesn't compete for bandwidth
  // with above-the-fold resources on initial load.
  const animInView = useInView(animRef, { once: true, margin: "200px" });

  return (
    <section
      data-testid="home-value"
      className="section-dark py-10 md:py-16 relative overflow-hidden"
    >
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-6">
          <p className="overline">Why GoHealthe</p>
          <motion.h2
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: [0.19, 1, 0.22, 1] }}
            className="font-serif text-4xl md:text-5xl mt-6 leading-[1.1]"
          >
            India's{" "}
            <span className="italic text-[#189A96]">
              Specialist Cladding Installation
            </span>{" "}
            Company for Healthcare, Cleanroom and Hospitality Facilities
          </motion.h2>
          <p className="mt-6 font-sans text-[19px] text-[#F8FAFC]/70 leading-relaxed">
            At GoHealthe, we specialise in transforming critical
            environments with precision cladding installation solutions.
            From hospitals and cleanrooms to pharmaceutical facilities and
            hospitality spaces, our expert installation services are
            designed to create hygienic, durable and easy-to-maintain
            interiors that meet the highest standards of performance,
            safety and quality.
          </p>
        </div>

        {/* Animation -- confined to the right column, desktop-only. */}
        <div
          ref={animRef}
          className="hidden md:block md:col-span-6 relative h-[360px] lg:h-[440px] overflow-hidden"
        >
          <div className="absolute inset-0" aria-hidden="true">
            {animInView && (
              <Suspense fallback={null}>
                <Hero3D />
              </Suspense>
            )}
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-[#06131F]/50 via-transparent to-[#06131F]/70 pointer-events-none" />
          <div className="absolute inset-0 grain pointer-events-none" />
        </div>
      </div>
    </section>
  );
}

// Draw-in state is controlled once, at the <svg> root, via a single
// IntersectionObserver -- child shapes just inherit "hidden"/"visible"
// through variants. Per-child whileInView was unreliable because nested
// SVG shape elements (circle/path with no box of their own) don't always
// report a measurable bounding rect to the observer.
const iconDrawVariants = {
  hidden: { pathLength: 0 },
  visible: { pathLength: 1 },
};

function AwardDrawIcon({ className }) {
  return (
    <motion.svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.8 }}
    >
      <motion.circle
        cx="12"
        cy="8"
        r="6"
        variants={iconDrawVariants}
        transition={{ duration: 1, ease: [0.19, 1, 0.22, 1] }}
      />
      <motion.path
        d="m15.477 12.89 1.515 8.526a.5.5 0 0 1-.81.47l-3.58-2.687a1 1 0 0 0-1.197 0l-3.586 2.686a.5.5 0 0 1-.81-.469l1.514-8.526"
        variants={iconDrawVariants}
        transition={{ duration: 0.7, delay: 0.85, ease: [0.19, 1, 0.22, 1] }}
      />
    </motion.svg>
  );
}

function BadgeCheckDrawIcon({ className }) {
  return (
    <motion.svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.8 }}
    >
      <motion.path
        d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"
        variants={iconDrawVariants}
        transition={{ duration: 1, ease: [0.19, 1, 0.22, 1] }}
      />
      <motion.path
        d="m9 12 2 2 4-4"
        variants={iconDrawVariants}
        transition={{ duration: 0.5, delay: 0.95, ease: [0.19, 1, 0.22, 1] }}
      />
    </motion.svg>
  );
}

const STAT_ICONS = { award: AwardDrawIcon, "badge-check": BadgeCheckDrawIcon };

function CountUp({ text, duration = 1.8 }) {
  const match = text.match(/^(\d+)(.*)$/);
  const target = match ? parseInt(match[1], 10) : 0;
  const suffix = match ? match[2] : text;
  const ref = useRef(null);
  // Only shrink the trigger zone vertically (delay until scrolled a bit
  // further into view) -- a symmetric margin also shrinks left/right,
  // which pushed elements near the screen edge (e.g. the left column on
  // narrow mobile viewports) outside the intersection zone entirely, so
  // their count-up never fired.
  const inView = useInView(ref, { once: true, margin: "0px 0px -80px 0px" });
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, target, {
      duration,
      ease: [0.19, 1, 0.22, 1],
      onUpdate: (v) => setCount(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, target, duration]);

  return (
    <span ref={ref}>
      {count}
      {suffix}
    </span>
  );
}

function StatsRow() {
  return (
    <section
      data-testid="home-stats"
      className="bg-[#F8FAFC] py-10 md:py-14 relative overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <p className="overline text-[#189A96] mb-10">Track record</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-y-10 gap-x-8 border-t border-[#0F2A44]/12 pt-10">
          {STATS.map((s, i) => {
            const Icon = s.icon ? STAT_ICONS[s.icon] : null;
            return (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.8,
                  delay: i * 0.08,
                  ease: [0.19, 1, 0.22, 1],
                }}
                data-testid={`stat-${i}`}
                className="text-center"
              >
                <div className="h-14 md:h-16 flex items-end justify-center">
                  {Icon ? (
                    <Icon className="w-11 h-11 md:w-12 md:h-12 text-[#189A96]" />
                  ) : (
                    <div className="editorial-num text-5xl md:text-7xl leading-none">
                      <CountUp text={s.value} />
                    </div>
                  )}
                </div>
                <p className="mt-5 font-sans text-sm text-[#0F2A44]/70 leading-relaxed max-w-[240px] mx-auto">
                  {s.label}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ServicesList() {
  const [hoverIdx, setHoverIdx] = useState(null);
  return (
    <section
      data-testid="home-services"
      className="bg-[#F8FAFC] py-28 md:py-40 relative"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16">
          <div className="md:col-span-6">
            <p className="overline">What we install</p>
            <h2 className="font-serif text-5xl md:text-6xl mt-6 leading-[1.02]">
              Six systems, one <span className="italic text-[#189A96]">standard</span>.
            </h2>
          </div>
          <div className="md:col-span-5 md:col-start-8 pt-6">
            <p className="font-sans text-[19px] text-[#0F2A44]/70 leading-relaxed">
              From single-room retrofits to full cleanroom fit-outs, every
              install passes through the same fabrication studio, the same
              QA leads and the same 14-year warranty.
            </p>
          </div>
        </div>

        {/* Row list -- no cards */}
        <div className="border-t border-[#0F2A44]/12">
          {SERVICES.map((s, i) => {
            const Row = s.slug ? Link : "div";
            return (
              <Row
                key={s.num}
                {...(s.slug ? { to: `/services/${s.slug}` } : {})}
                onMouseEnter={() => setHoverIdx(i)}
                onMouseLeave={() => setHoverIdx(null)}
                className={`row-line group grid grid-cols-12 items-center py-8 md:py-10 relative transition-colors duration-500 hover:bg-white ${
                  s.slug ? "cursor-pointer" : "cursor-default"
                }`}
                data-testid={`service-row-${s.num}`}
              >
                <div className="col-span-2 md:col-span-1 overline text-[#189A96]">
                  {s.num}
                </div>
                <div className="col-span-10 md:col-span-5">
                  <h3 className="font-serif text-3xl md:text-4xl text-[#0F2A44] group-hover:text-[#189A96] transition-colors">
                    {s.title}
                  </h3>
                </div>
                <div className="col-span-12 md:col-span-4 mt-4 md:mt-0">
                  <p className="font-sans text-sm text-[#0F2A44]/70 leading-relaxed">
                    {s.detail}
                  </p>
                </div>
                <div className="col-span-12 md:col-span-2 mt-4 md:mt-0 flex md:justify-end">
                  <ArrowUpRight
                    className={`w-5 h-5 transition-transform duration-500 ${
                      hoverIdx === i ? "translate-x-1 -translate-y-1 text-[#189A96]" : "text-[#0F2A44]/40"
                    }`}
                  />
                </div>
              </Row>
            );
          })}
        </div>

        <div className="mt-16 flex justify-start">
          <Link to="/services" className="btn-ghost" data-testid="services-see-all">
            Full services breakdown <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

function InstallProgressStrip() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const timer = setInterval(() => {
      setActive((i) => (i + 1) % PROGRESS_SLIDES.length);
    }, PROGRESS_SLIDE_DURATION);
    return () => clearInterval(timer);
  }, [active, paused]);

  const slide = PROGRESS_SLIDES[active];

  return (
    <>
      {/* Heading -- lives outside the image now, no longer overlapping the
          carousel/drag handles. */}
      <section className="bg-[#F8FAFC] pb-6 md:pb-8">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.19, 1, 0.22, 1] }}
            className="overline text-[#189A96]"
          >
            Site progress
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.19, 1, 0.22, 1] }}
            className="font-serif text-4xl md:text-6xl mt-4 leading-[1.05] text-[#0F2A44]"
          >
            Transformation
            <br />
            in <span className="italic text-[#189A96]">motion</span>.
          </motion.h2>

          <div className="mt-8 flex justify-center gap-3 ">
            {PROGRESS_TABS.map((tab, i) => (
              <button
                key={tab}
                type="button"
                onClick={() => setActive(i)}
                data-testid={`progress-tab-${i}`}
                className={`px-6 py-2.5 font-sans text-sm font-semibold tracking-[0.08em] rounded-2xl uppercase border transition-colors duration-300 ${
                  active === i
                    ? "bg-[#189A96] border-[#189A96] text-white"
                    : "border-[#0F2A44]/20 text-[#0F2A44]/70 hover:border-[#189A96]/50"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section
        data-testid="home-progress-strip"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(true)}
        onTouchStart={() => setPaused(true)}
        className="relative w-full aspect-[4/3] md:aspect-auto md:h-[90svh] overflow-hidden bg-[#06131F]"
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={slide.id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6, ease: [0.19, 1, 0.12, 1] }}
            className="absolute inset-0"
          >
            {slide.kind === "triple" ? (
              <TripleImageSlider images={slide.images} alts={slide.alts} className="h-full" />
            ) : (
              <BeforeAfterSlider
                beforeSrc={slide.before}
                afterSrc={slide.after}
                aspectClassName="h-full"
              />
            )}
          </motion.div>
        </AnimatePresence>

        {/* Carousel indicators — active dot fills over the 10s dwell time */}
      <div
        data-testid="progress-carousel-dots"
        className="absolute bottom-6 md:bottom-8 left-1/2 -translate-x-1/2 z-20 flex items-center gap-3"
      >
        {PROGRESS_SLIDES.map((s, i) => (
          <button
            key={s.id}
            type="button"
            onClick={() => setActive(i)}
            aria-label={`Show project ${i + 1}`}
            data-testid={`progress-dot-${i}`}
            className="relative w-10 md:w-12 h-1.5 rounded-full bg-white/25 overflow-hidden"
          >
            {active === i && (
              <motion.div
                key={active}
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{
                  duration: PROGRESS_SLIDE_DURATION / 1000,
                  ease: "linear",
                }}
                className="absolute inset-0 bg-[#189A96] origin-left"
              />
            )}
          </button>
        ))}
        </div>
      </section>
    </>
  );
}

function IndustriesSplit() {
  const [active, setActive] = useState(0);
  const imageRef = useRef(null);

  // On mobile the image column sits stacked below the tab list instead of
  // side-by-side, so tapping a tab silently swaps an image the user can't
  // see without then scrolling down themselves. Bring it into view for them.
  const selectIndustry = (i) => {
    setActive(i);
    if (window.matchMedia("(max-width: 767px)").matches) {
      imageRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  return (
    <section
      data-testid="home-industries"
      className="section-dark py-10 md:py-16 relative overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="mb-10">
          <p className="overline">Where we work</p>
          <h2 className="font-serif text-5xl md:text-6xl mt-6 leading-[1.02] text-[#F8FAFC] max-w-3xl">
            Six industries.
            <br />
            One <span className="italic text-[#189A96]">uncompromising</span> spec.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          {/* Left: index */}
          <div className="md:col-span-5">
            <div className="border-t border-white/10">
              {INDUSTRIES.map((ind, i) => (
                <button
                  key={ind.key}
                  onMouseEnter={() => setActive(i)}
                  onFocus={() => setActive(i)}
                  onClick={() => selectIndustry(i)}
                  data-testid={`industry-tab-${ind.key}`}
                  className={`row-line w-full text-left py-6 md:py-8 flex items-baseline gap-6 transition-colors ${
                    active === i ? "text-[#189A96]" : "text-[#F8FAFC]"
                  }`}
                >
                  <div className="flex-1">
                    <div className="font-serif text-3xl md:text-4xl leading-none">
                      {ind.label}
                    </div>
                    <div className="mt-1 font-sans text-xs text-[#F8FAFC]/50">
                      {ind.subtitle}
                    </div>
                  </div>
                  <ArrowUpRight
                    className={`w-4 h-4 shrink-0 transition-transform ${
                      active === i ? "translate-x-1 -translate-y-1" : ""
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Right: sticky image + copy */}
          <div className="md:col-span-6 md:col-start-7" ref={imageRef}>
            <div className="sticky top-28">
              <div className="relative aspect-[4/5] overflow-hidden">
                {INDUSTRIES.map((ind, i) => (
                  <motion.img
                    key={ind.key}
                    src={ind.image}
                    alt={ind.label}
                    loading="lazy"
                    initial={false}
                    animate={{
                      opacity: active === i ? 1 : 0,
                      scale: active === i ? 1 : 1.08,
                    }}
                    transition={{ duration: 0.9, ease: [0.19, 1, 0.22, 1] }}
                    className="absolute inset-0 w-full h-full"
                  />
                ))}
                <div className="absolute inset-0 bg-gradient-to-t" />
                <div className="absolute bottom-6 left-6 right-6">
                  {/* <p className="font-sans text-sm text-[#F8FAFC]/90 leading-relaxed">
                    {INDUSTRIES[active].copy}
                  </p> */}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-10">
          <Link to="/clients" className="btn-ghost" data-testid="industries-see-all">
            Every sector we serve <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

function ProcessSection() {
  const listRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: listRef,
    offset: ["start 0.8", "end 0.45"],
  });

  return (
    <section
      data-testid="home-process"
      className="bg-[#F8FAFC] py-10 md:py-16 relative"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <div className="sticky top-28">
            <h3 className="overline">The process</h3>
            <h2 className="font-serif text-5xl md:text-6xl mt-6 leading-[1.02]">
              From spec
              <br />
              to <span className="italic text-[#189A96]">handover</span>.
            </h2>
            <h4 className="mt-10 font-serif text-2xl md:text-3xl text-[#0F2A44] leading-snug">
              Trusted Installation Partner for Critical Environments
            </h4>
            <p className="mt-4 font-sans text-[19px] text-[#0F2A44]/70 leading-relaxed">
              When hygiene, compliance and long-term durability matter,
              installation quality makes all the difference.
            </p>
            <p className="mt-4 font-sans text-[19px] text-[#0F2A44]/70 leading-relaxed">
              Our trained installation specialists execute high-performance
              cladding and flooring systems with precision, ensuring
              seamless finishes, easy maintenance and long-term performance
              for demanding healthcare and industrial environments.
            </p>
          </div>
        </div>
        <div className="md:col-span-7 md:col-start-6">
          <div ref={listRef} className="relative">
            {/* Track */}
            <div className="absolute left-3.5 md:left-4 top-3 bottom-3 w-px bg-[#0F2A44]/12" />
            {/* Progress fill, tied to scroll */}
            <motion.div
              style={{ scaleY: scrollYProgress }}
              className="absolute left-3.5 md:left-4 top-3 bottom-3 w-px bg-[#189A96] origin-top"
            />
            {PROCESS.map((p, i) => (
              <motion.div
                key={p.num}
                initial={{ opacity: 0, x: 24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.6 }}
                transition={{
                  duration: 0.6,
                  delay: i * 0.05,
                  ease: [0.19, 1, 0.22, 1],
                }}
                className="relative flex items-start gap-5 md:gap-6 py-6 border-b border-[#0F2A44]/12 last:border-b-0 group"
              >
                <span className="relative z-10 shrink-0 w-7 h-7 md:w-8 md:h-8 rounded-full bg-[#F8FAFC] border border-[#0F2A44]/25 flex items-center justify-center font-sans text-[11px] font-semibold text-[#0F2A44]/50 group-hover:border-[#189A96] group-hover:bg-[#189A96]/10 group-hover:text-[#189A96] transition-colors duration-300">
                  {p.num}
                </span>
                <span className="font-serif text-2xl md:text-3xl text-[#0F2A44] pt-0.5 group-hover:text-[#189A96] transition-colors duration-300">
                  {p.label}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function EditorialImage() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [-60, 60]);

  return (
    <section
      ref={ref}
      data-testid="home-editorial"
      className="relative h-[75svh] md:h-[95svh] overflow-hidden bg-[#06131F]"
    >
      <motion.img
        src={IMAGES.hospitalCorridor}
        alt="Hygienic corridor"
        style={{ y }}
        className="absolute inset-0 w-full h-[120%] object-cover opacity-70"
      />
      <div className="absolute inset-0 bg-[#06131F]/40" />
      <div className="relative z-10 h-full max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col justify-end pb-16 md:pb-24">
        <h2 className="font-serif text-5xl md:text-7xl text-[#F8FAFC] mt-6 leading-[1] max-w-4xl">
          Over 510,000 Sq. Ft. of
          <span className="italic text-[#189A96]"> Hygienic Cladding Installed</span>
        </h2>
      </div>
    </section>
  );
}

function CTAFooter() {
  return (
    <section
      data-testid="home-cta"
      className="bg-[#F8FAFC] py-10 md:py-16 relative"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
        <div className="md:col-span-8">
          <p className="overline">Next step</p>
          <h2 className="font-serif text-5xl md:text-7xl mt-6 leading-[1]">
            Bring us your
            <br />
            <span className="italic text-[#189A96]">hardest room</span>.
          </h2>
        </div>
        <div className="md:col-span-4 flex md:justify-end">
          <Link
            to="/contact"
            data-testid="home-cta-btn"
            className="btn-primary"
          >
            Request a quote <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div data-testid="home-page">
      <HeroGallery />
      {/* <Marquee /> */}
      <Molecular3DBreak />
      <StatsRow />
      <ValueProp />
      <InstallCards />
      <InstallProgressStrip />
      <IndustriesSplit />
      {/* <EditorialImage /> -- "510,000 Sq. Ft." now shown as a stat card in StatsRow instead */}
      <ProcessSection />
      <CTAFooter />
    </div>
  );
}
